import React from 'react';
import { DatePicker } from 'antd';
import { Rule } from 'antd/es/form';
import dayjs, { Dayjs } from 'dayjs';
import styled from 'styled-components';

import Icon from '../../icons/Icon';

import { FormFieldLabel } from './FormFieldLabel';
import { FormInputProps, StyledFormItem } from './FormInput';

export const MONTH_PICKER_DISPLAY_FORMAT = 'MMM YYYY';
export const MONTH_PICKER_PAYLOAD_FORMAT = 'YYYY-MM';
export const PROFESSIONAL_EXPERIENCE_MIN_MONTH = dayjs('1950-01', MONTH_PICKER_PAYLOAD_FORMAT);

const DatePickerIcon = styled(Icon).attrs({
  name: 'DatePicker',
})`
  vertical-align: -0.2rem;
`;

export const isMonthOutsideRange = (
  value?: Dayjs | null,
  minMonth: Dayjs = PROFESSIONAL_EXPERIENCE_MIN_MONTH,
  maxMonth: Dayjs = dayjs()
) => {
  if (!value || !value.isValid()) {
    return false;
  }

  return value.isBefore(minMonth, 'month') || value.isAfter(maxMonth, 'month');
};

export const createMonthPickerRangeRule = (
  minMonth: Dayjs = PROFESSIONAL_EXPERIENCE_MIN_MONTH,
  maxMonth: Dayjs = dayjs()
): Rule => ({
  validator: async (_: unknown, value?: Dayjs | null) => {
    if (!value) {
      return;
    }

    if (!dayjs.isDayjs(value) || !value.isValid() || isMonthOutsideRange(value, minMonth, maxMonth)) {
      throw new Error(
        `Choose a month between ${minMonth.format(MONTH_PICKER_DISPLAY_FORMAT)} and ${maxMonth.format(
          MONTH_PICKER_DISPLAY_FORMAT
        )}.`
      );
    }
  },
});

type FormMonthPickerProps = Pick<FormInputProps, 'name' | 'label' | 'required' | 'allowClear' | 'disabled' | 'rules'> & {
  minMonth?: Dayjs;
  maxMonth?: Dayjs;
};

export const FormMonthPicker: React.FC<FormMonthPickerProps> = ({
  name,
  label,
  required,
  allowClear = true,
  disabled,
  rules = [],
  minMonth = PROFESSIONAL_EXPERIENCE_MIN_MONTH,
  maxMonth = dayjs(),
}) => {
  const rangeRule = createMonthPickerRangeRule(minMonth, maxMonth);

  return (
    <StyledFormItem
      name={name}
      style={{ marginBottom: 5, width: '100%' }}
      label={<FormFieldLabel required={required}>{label}</FormFieldLabel>}
      rules={[rangeRule, ...rules]}
    >
      <DatePicker
        picker="month"
        format={MONTH_PICKER_DISPLAY_FORMAT}
        style={{ width: '100%' }}
        suffixIcon={<DatePickerIcon />}
        allowClear={allowClear}
        disabled={disabled}
        inputReadOnly={false}
        disabledDate={(value) => isMonthOutsideRange(value, minMonth, maxMonth)}
      />
    </StyledFormItem>
  );
};
